/**
 * SQLite's `datetime('now')` returns UTC in the shape "YYYY-MM-DD HH:MM:SS"
 * with no timezone marker. Browsers parse strings in that exact shape as
 * LOCAL time, which makes "started_at" appear hours into the future for
 * users west of UTC — showing wall-clock times that haven't happened yet
 * and producing negative "Xs ago" deltas. Normalise to ISO-8601 UTC before
 * handing to `Date()` so it's always read as UTC.
 */
function parseTimestamp(s: string): Date {
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(s)) {
    return new Date(s.replace(" ", "T") + "Z");
  }
  return new Date(s);
}

/** Friendly absolute date+time. Falls back to ISO if Intl chokes. */
export function formatDateTime(iso: string, timezone?: string): string {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(parseTimestamp(iso));
  } catch {
    return iso;
  }
}

/** Compact "5m ago", "2h ago", "3d ago", or absolute date for older items. */
export function formatRelative(iso: string): string {
  const ms = Date.now() - parseTimestamp(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 14) return `${d}d ago`;
  return formatDateTime(iso);
}

export function formatMoney(
  cents: number,
  currency: string,
  locale = "es-CR",
): string {
  const amount = cents / 100;
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      currencyDisplay: "code",
      minimumFractionDigits: currency === "CRC" ? 0 : 2,
      maximumFractionDigits: currency === "CRC" ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}
